import type {
  FileResponse,
  FileVersionPrunePreview,
  FileVersionPruneResult,
  IndexJobLiveStatus,
  IndexJobResponse,
  MarkdownPreviewResponse,
  StopIndexResponse,
  UploadFileResult,
} from '../types/api'
import { ApiClientError, apiClient } from './client'

export type { UploadFileResult }

function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : ''
}

export async function getProjectFiles(projectId: number): Promise<FileResponse[]> {
  return apiClient.get<FileResponse[]>(`/projects/${projectId}/files`)
}

export interface UploadMeta {
  docType: string
  tags: string[]
}

export interface TagSuggestion {
  name: string
  usageCount: number
}

export async function getProjectTags(projectId: number): Promise<TagSuggestion[]> {
  return apiClient.get<TagSuggestion[]>(`/projects/${projectId}/tags`)
}

export async function uploadFile(
  projectId: number,
  file: File,
  meta: UploadMeta
): Promise<UploadFileResult> {
  try {
    const result = await apiClient.upload<UploadFileResult>(
      `/projects/${projectId}/files/upload`,
      file,
      { docType: meta.docType, tags: JSON.stringify(meta.tags) }
    )
    return { ...result, duplicate: result.duplicate ?? false }
  } catch (error) {
    if (error instanceof ApiClientError && error.status === 409) {
      if (error.apiError.code === 'INDEXING_IN_PROGRESS') {
        throw error
      }
      // Back-compat: older API returned 409 for duplicate content in the same project
      return {
        id: 0,
        originalFilename: file.name,
        fileType: fileExtension(file.name),
        fileSize: file.size,
        sha256Hash: null,
        indexingStatus: 'pending',
        duplicate: true,
        message: 'Already uploaded (same content)',
      }
    }
    throw error
  }
}

export async function replaceFile(fileId: number, file: File): Promise<UploadFileResult> {
  const result = await apiClient.upload<UploadFileResult>(`/files/${fileId}/replace`, file)
  return { ...result, duplicate: false }
}

export async function downloadFile(fileId: number, filename: string): Promise<void> {
  await apiClient.download(`/files/${fileId}/download`, filename)
}

export async function deleteFile(fileId: number): Promise<{ deleted: boolean }> {
  return apiClient.delete<{ deleted: boolean }>(`/files/${fileId}`)
}

export async function previewFileVersionPrune(projectId: number): Promise<FileVersionPrunePreview> {
  return apiClient.get<FileVersionPrunePreview>(`/projects/${projectId}/files/prune-versions`)
}

export async function pruneFileVersions(projectId: number): Promise<FileVersionPruneResult> {
  return apiClient.post<FileVersionPruneResult>(`/projects/${projectId}/files/prune-versions`, {})
}

export async function indexProject(
  projectId: number,
  fileIds?: number[]
): Promise<IndexJobResponse> {
  const body = fileIds && fileIds.length > 0 ? { fileIds } : {}
  return apiClient.post<IndexJobResponse>(`/projects/${projectId}/index`, body)
}

export async function stopProjectIndex(projectId: number): Promise<StopIndexResponse> {
  return apiClient.post<StopIndexResponse>(`/projects/${projectId}/index/stop`, {})
}

export async function reindexProject(
  projectId: number,
  fileIds?: number[]
): Promise<IndexJobResponse> {
  const body = fileIds && fileIds.length > 0 ? { fileIds } : {}
  return apiClient.post<IndexJobResponse>(`/projects/${projectId}/reindex`, body)
}

export async function reindexFile(fileId: number): Promise<IndexJobResponse> {
  return apiClient.post<IndexJobResponse>(`/files/${fileId}/reindex`, {})
}

export async function getActiveJob(projectId: number): Promise<IndexJobLiveStatus | null> {
  const result = await apiClient.get<{ job: IndexJobLiveStatus | null }>(
    `/projects/${projectId}/active-job`
  )
  return result.job
}

export async function getJobStatus(jobId: number): Promise<IndexJobLiveStatus> {
  return apiClient.get(`/jobs/${jobId}`)
}

export async function getFileMarkdown(fileId: number): Promise<MarkdownPreviewResponse> {
  return apiClient.get<MarkdownPreviewResponse>(`/files/${fileId}/markdown`)
}

export async function downloadMarkdown(fileId: number, filename: string): Promise<void> {
  const base = filename.replace(/\.[^.]+$/, '') || 'document'
  await apiClient.download(`/files/${fileId}/download-markdown`, `${base}.md`)
}

// ── Transcript Distillation ──────────────────────────────────────────

export interface DistillProposal {
  id: number
  status: string
  stats: {
    rawChunks: number
    segments: number
    dropped: number
    decisions: number
    actionItems: number
    charsBefore: number
    charsAfter: number
    reductionPct: number
    model: string
    promptVersion: string
    llmTokens: number
  }
  payload: {
    segments: Array<{
      sourceChunkIndex: number
      notes: string
      entities: string[]
      rawTurns?: string
      sectionPath?: string
    }>
    droppedSegments: Array<{
      sourceChunkIndex: number
      rawTurns: string
      reason: string
    }>
    decisions: Array<{
      statement: string
      owner?: string | null
      due?: string | null
      speaker?: string | null
      sourceChunkIndex: number
      quote?: string
    }>
    actionItems: Array<{
      statement: string
      owner?: string | null
      due?: string | null
      speaker?: string | null
      sourceChunkIndex: number
      quote?: string
    }>
  }
  sourceGeneration: number
  createdAt: string
}

export async function distillFile(fileId: number): Promise<{ jobId: number }> {
  return apiClient.post(`/files/${fileId}/distill`, {})
}

export async function getDistillProposal(fileId: number): Promise<DistillProposal | null> {
  try {
    return await apiClient.get<DistillProposal>(`/files/${fileId}/distill/proposal`)
  } catch (error) {
    // 204 No Content → apiClient.get throws SyntaxError (empty body parse)
    if (error instanceof SyntaxError) {
      return null
    }
    throw error
  }
}

export async function acceptDistillProposal(
  fileId: number,
  proposalId: number
): Promise<{ applied: boolean; chunkCount: number }> {
  return apiClient.post(`/files/${fileId}/distill/accept`, { proposalId })
}

export async function rejectDistillProposal(
  fileId: number,
  proposalId: number
): Promise<{ rejected: boolean }> {
  return apiClient.post(`/files/${fileId}/distill/reject`, { proposalId })
}
